import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import { createCopyButton, copySelectionWithin, type ClipboardLike } from "./copy-as-markdown.ts";
import type { MessageRewindTarget } from "./message-transcript.ts";

export type MessageActionHandlers = {
  onEditUser?: (target: MessageRewindTarget, draft: string) => void;
  onRegenerate?: () => void;
  /**
   * Toggle the per-message manual collapse. When the message is currently
   * collapsed, the button is rendered as "Expand"; when expanded, "Collapse".
   * Implementation lives in the parent (which owns the collapsed-state set)
   * so the same state can drive the bubble's preview line.
   */
  onToggleCollapse?: () => void;
  /**
   * Whether the message is currently in the manual-collapsed state. When
   * `true`, the action button is rendered as "Expand"; otherwise "Collapse".
   */
  isCollapsed?: boolean;
  /**
   * Optional: returns the DOM root for the bubble that owns these actions.
   * The "Copy selection" button uses it to scope `getSelection()` so the
   * copied text never bleeds outside the bubble (e.g. into the composer
   * textarea). When omitted, "Copy selection" is not rendered.
   */
  bubbleScope?: () => Element | Document | null;
  /**
   * Optional clipboard override used by tests; defaults to
   * `navigator.clipboard` at call time.
   */
  clipboard?: ClipboardLike;
};

export function renderMessageActions(params: {
  role: "user" | "assistant" | "other";
  markdown?: string | null;
  rewindTarget?: MessageRewindTarget | null;
  userDraft?: string | null;
  isLastAssistant?: boolean;
  isCollapsed?: boolean;
  handlers?: MessageActionHandlers;
}): ReturnType<typeof html> | typeof nothing {
  const handlers = params.handlers;
  if (!handlers) {
    return nothing;
  }

  const canCopy = Boolean(params.markdown?.trim());
  const canEdit =
    params.role === "user" && Boolean(params.rewindTarget) && Boolean(handlers.onEditUser);
  const canRegenerate =
    params.role === "assistant" && params.isLastAssistant && Boolean(handlers.onRegenerate);
  const canCopySelection = canCopy && typeof handlers.bubbleScope === "function";
  const canToggleCollapse = Boolean(handlers.onToggleCollapse) && Boolean(params.markdown?.trim());

  if (!canCopy && !canEdit && !canRegenerate && !canCopySelection && !canToggleCollapse) {
    return nothing;
  }

  const idleLabel = "Copy";
  return html`
    <div class="chat-message-actions" role="toolbar" aria-label="Message actions">
      ${
        canEdit
          ? html`
              <button
                type="button"
                class="chat-message-actions__btn"
                title="Edit this message (rewind on send after you confirm)"
                aria-label="Edit message"
                @click=${() => handlers.onEditUser?.(params.rewindTarget!, params.userDraft ?? "")}
              >
                ${icons.edit}
                <span class="sr-only">Edit</span>
              </button>
            `
          : nothing
      }
      ${
        canRegenerate
          ? html`
              <button
                type="button"
                class="chat-message-actions__btn"
                title="Regenerate this reply"
                aria-label="Regenerate reply"
                @click=${() => handlers.onRegenerate?.()}
              >
                ${icons.refreshCw}
                <span class="sr-only">Regenerate</span>
              </button>
            `
          : nothing
      }
      ${
        canToggleCollapse
          ? html`
              <button
                type="button"
                class="chat-message-actions__btn chat-message-actions__btn--collapse"
                title=${params.isCollapsed ? "Expand this message" : "Collapse this message to a preview"}
                aria-label=${params.isCollapsed ? "Expand message" : "Collapse message"}
                aria-pressed=${params.isCollapsed ? "true" : "false"}
                data-testid="chat-message-collapse"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  handlers.onToggleCollapse?.();
                }}
              >
                ${params.isCollapsed ? icons.maximize : icons.minimize}
                <span class="sr-only">${params.isCollapsed ? "Expand" : "Collapse"}</span>
              </button>
            `
          : nothing
      }
      ${
        canCopy
          ? createCopyButton({
              text: () => params.markdown!.trim(),
              label: idleLabel,
              className: "chat-message-actions__btn",
            })
          : nothing
      }
      ${
        canCopySelection
          ? html`
              <button
                type="button"
                class="chat-message-actions__btn chat-message-actions__btn--copy-selection"
                title="Copy only the highlighted text in this message"
                aria-label="Copy selection"
                data-testid="chat-message-copy-selection"
                @click=${async (e: Event) => {
                  e.stopPropagation();
                  const btn = e.currentTarget as HTMLButtonElement | null;
                  const scope = handlers.bubbleScope?.() ?? null;
                  const copied = await copySelectionWithin(scope, handlers.clipboard);
                  if (!btn) return;
                  btn.dataset.copied = copied ? "1" : "0";
                  btn.setAttribute("aria-live", "polite");
                  window.setTimeout(() => {
                    if (!btn.isConnected) return;
                    delete btn.dataset.copied;
                  }, 1500);
                }}
              >
                ${icons.copySelection}
                <span class="sr-only">Copy selection</span>
              </button>
            `
          : nothing
      }
    </div>
  `;
}
